import {AfterViewInit, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation, input, output} from '@angular/core';
import {FormsModule} from '@angular/forms';

export type RenameSource = 'blur' | 'explicit';

export interface RenameRequest {
    name: string;
    source: RenameSource;
}

/**
 * Inline name editor for a sidebar row — a folder or a page.
 *
 * Extracted from `dragdrop-tree` so the blur-vs-click ordering lives in one place instead of
 * being sequenced by a boolean on a 650-line component. The ✓/⊘ affordances are INSIDE this
 * component and call `preventDefault()` on mousedown, which stops the browser moving focus off
 * the input at all — so no blur fires and there is nothing to suppress. That is what replaces
 * the old `suppressNextBlur` flag, which both F48 (the flag leaking across rename sessions) and
 * F87 (the flag leaking on a refused re-entrant save, silently dropping the typed name) lived on.
 *
 * Deliberately dumb. It owns the draft text and the focus mechanics; validation, the PATCH, the
 * optimistic update and the in-flight guard stay with the parent, which is where the entity and
 * its API live. It never closes itself either: the parent decides, because only the parent knows
 * whether the name was acceptable.
 *
 * ✓/⊘ are `<span>`, not `<button>`, carried over from F91 — making them real buttons is now safe
 * (preventDefault means they never take focus) but is an accessibility change, not this ticket.
 */
@Component({
    selector: 'entity-name-editor',
    imports: [FormsModule],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './entity-name-editor.component.html',
    styleUrls: ['./entity-name-editor.component.scss'],
})
export class EntityNameEditorComponent implements OnInit, AfterViewInit {
    @ViewChild('nameInput', {read: ElementRef}) nameInput?: ElementRef<HTMLInputElement>;

    /** Name to start editing from. Read once, on init — the parent owns the committed value. */
    name = input<string>('');

    save = output<RenameRequest>();
    cancel = output<void>();

    draft: string = '';

    ngOnInit(): void {
        this.draft = this.name();
    }

    ngAfterViewInit(): void {
        const el = this.nameInput?.nativeElement;
        el?.focus();
        el?.select();
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.save.emit({name: this.draft, source: 'explicit'});
        } else if (event.key === 'Escape') {
            event.preventDefault();
            // The sidebar is a <mat-sidenav>, and MatDrawer listens for ESCAPE on its own element
            // with no `disableClose` and no defaultPrevented check (material/sidenav drawer.ts) —
            // so an un-stopped Escape cancels the rename AND closes the whole sidebar, losing the
            // user's place mid folder-tidying. preventDefault alone does not stop it; the event has
            // to stop bubbling. Pre-existing behaviour of the folder rename, fixed here because
            // page rename made it a second way to hit it.
            event.stopPropagation();
            this.cancel.emit();
        }
    }

    onBlur(): void {
        this.save.emit({name: this.draft, source: 'blur'});
    }

    /**
     * Keep focus in the input when the pointer goes down on ✓/⊘.
     *
     * Preventing mousedown's default action is what stops the browser blurring the input, so the
     * click that follows is the only event that acts — and it acts as an EXPLICIT save.
     *
     * Without it the blur fires first and saves as a `blur`, then destroys the editor before the
     * click can land. That is not a double save (measured — the ✓ is gone by mouseup), but the two
     * sources are treated differently on purpose: an explicit save keeps the editor open on an
     * invalid name so it can be corrected, a blur save closes it and discards the correction. So
     * dropping this binding silently turns every ✓ into a click-away. Do not remove it without
     * reading F48/F87.
     */
    keepFocus(event: MouseEvent): void {
        event.preventDefault();
    }

    onSaveClick(event: MouseEvent): void {
        event.stopPropagation();
        this.save.emit({name: this.draft, source: 'explicit'});
    }

    onCancelClick(event: MouseEvent): void {
        event.stopPropagation();
        this.cancel.emit();
    }
}
